import { useMemo, useState, type JSX } from 'react'
import { NavLink, useLocation } from 'react-router'
import {
  Building2,
  Shield,
  Zap,
  AlertTriangle,
  FileText,
  Users,
  Key,
  History,
  Flag,
  Database,
  Sprout,
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore.js'

const EMPTY_PERMS: string[] = []

export type NavLeaf = { to: string; label: string; icon: React.ReactNode; requirePermission?: string }
export type NavSection = {
  sectionLabel: string
  icon: React.ReactNode
  requirePermission?: string
  items: NavLeaf[]
}

const NAV: NavSection[] = [
  {
    sectionLabel: 'Tenant Management',
    icon: <Building2 className="w-5 h-5" />,
    requirePermission: 'platform:tenants_manage',
    items: [
      { to: '/platform/tenants', label: 'Tenants', icon: <Users className="w-4 h-4" /> },
      { to: '/platform/plans', label: 'Plans', icon: <FileText className="w-4 h-4" /> },
      { to: '/platform/tenant-privileges', label: 'Tenant Privileges', icon: <Shield className="w-4 h-4" /> },
      { to: '/platform/tenant-admins', label: 'Tenant Admins', icon: <Users className="w-4 h-4" /> },
      {
        to: '/platform/admin-roles',
        label: 'Admin Roles',
        icon: <Shield className="w-4 h-4" />,
        requirePermission: 'platform:roles_manage',
      },
    ],
  },
  {
    sectionLabel: 'RBAC',
    icon: <Key className="w-5 h-5" />,
    requirePermission: 'platform:rbac_manage',
    items: [
      {
        to: '/platform/roles',
        label: 'Roles',
        icon: <Shield className="w-4 h-4" />,
      },
      {
        to: '/platform/permissions',
        label: 'Permissions',
        icon: <Key className="w-4 h-4" />,
      },
      {
        to: '/platform/rbac-audit',
        label: 'Audit Logs',
        icon: <History className="w-4 h-4" />,
      },
    ],
  },
  {
    sectionLabel: 'Security',
    icon: <Shield className="w-5 h-5" />,
    items: [
      {
        to: '/platform/mfa',
        label: 'My MFA',
        icon: <Key className="w-4 h-4" />,
      },
      {
        to: '/platform/key-rotation',
        label: 'Key Rotation',
        icon: <Key className="w-4 h-4" />,
        requirePermission: 'platform:key_rotation',
      },
      {
        to: '/platform/audit-log',
        label: 'Audit Log',
        icon: <History className="w-4 h-4" />,
        requirePermission: 'platform:audit_view',
      },
      {
        to: '/platform/change-log',
        label: 'Change Log',
        icon: <FileText className="w-4 h-4" />,
        requirePermission: 'platform:audit_view',
      },
    ],
  },
  {
    sectionLabel: 'Runtime',
    icon: <Zap className="w-5 h-5" />,
    items: [
      {
        to: '/platform/feature-flags',
        label: 'Feature Flags',
        icon: <Flag className="w-4 h-4" />,
        requirePermission: 'platform:feature_flags_admin',
      },
      {
        to: '/platform/incidents',
        label: 'Incidents',
        icon: <AlertTriangle className="w-4 h-4" />,
        requirePermission: 'platform:incidents_admin',
      },
      {
        to: '/platform/db-manager',
        label: 'Database Manager',
        icon: <Database className="w-4 h-4" />,
        requirePermission: 'platform:tenants_manage',
      },
      {
        to: '/platform/seed-data',
        label: 'Data Seeding',
        icon: <Sprout className="w-4 h-4" />,
        requirePermission: 'platform:tenants_manage',
      },
    ],
  },
]

interface PlatformSidebarProps {
  activeSection: NavSection | null;
  onSectionChange: (section: NavSection) => void;
}

export default function PlatformSidebar({ activeSection, onSectionChange }: PlatformSidebarProps): JSX.Element {
  const hasPermission = useAuthStore((s) => s.hasPermission)
  const permissions = useAuthStore((s) => s.user?.permissions ?? EMPTY_PERMS)
  const location = useLocation()
  /*
    The rail scrolls once the section list outgrows the viewport, and a scroll
    container clips on BOTH axes — `overflow-y: auto` forces `overflow-x` to
    `auto` too, so a tooltip positioned `left-full` would be cut off at the
    56px rail edge. The tooltip is therefore rendered once, outside the
    scroller, at fixed coordinates measured from the hovered button.
  */
  const [railTip, setRailTip] = useState<{ label: string; x: number; y: number } | null>(null)

  const allowedSections = useMemo(() => {
    return NAV.filter(sec => {
      // Check section-level permission
      if (sec.requirePermission && !hasPermission(sec.requirePermission)) {
        return false
      }
      // Check if any items in the section are accessible
      const hasAccessibleItems = sec.items.some(item =>
        !item.requirePermission || hasPermission(item.requirePermission)
      )
      return hasAccessibleItems
    })
  }, [hasPermission])

  // Determine active section based on current path
  const currentActiveSection = useMemo(() => {
    for (const section of allowedSections) {
      if (section.items.some(item => location.pathname === item.to || location.pathname.startsWith(item.to + '/'))) {
        return section
      }
    }
    return activeSection || allowedSections[0]
  }, [location.pathname, allowedSections, activeSection])

  return (
    <div className="flex h-full">
      {/* RAIL SIDEBAR */}
      <div
        className="w-[56px] flex-shrink-0 bg-[#f8f9fa] border-r border-gray-100 flex flex-col items-center py-3 z-30 h-full min-h-0 overflow-y-auto overflow-x-hidden nav-scroll"
        onScroll={() => setRailTip(null)}
      >
        {allowedSections.map((section) => {
          const isActive = currentActiveSection?.sectionLabel === section.sectionLabel
          const showTip = (event: { currentTarget: HTMLElement }): void => {
            const r = event.currentTarget.getBoundingClientRect()
            setRailTip({ label: section.sectionLabel, x: r.right + 12, y: r.top + r.height / 2 })
          }
          return (
            <div key={section.sectionLabel} className="relative shrink-0 mb-1">
              <button
                onClick={() => onSectionChange(section)}
                onMouseEnter={showTip}
                onMouseLeave={() => setRailTip(null)}
                onFocus={showTip}
                onBlur={() => setRailTip(null)}
                className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-[#3454cf]/10 text-[#3454cf] shadow-[0_0_0_1px_rgba(52,84,207,0.15)_inset]'
                    : 'text-gray-400 hover:bg-white hover:text-gray-600 hover:shadow-sm'
                }`}
                title={section.sectionLabel}
              >
                {section.icon}
              </button>
              {/* Active left pill — orange accent */}
              {isActive && (
                <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full" style={{ background: '#f6931c' }} />
              )}
            </div>
          )
        })}
      </div>

      {/* Rail tooltip — fixed so the scrolling rail cannot clip it */}
      {railTip && (
        <div
          role="presentation"
          className="fixed -translate-y-1/2 px-3 py-2 bg-gray-900/95 backdrop-blur-sm text-white text-xs whitespace-nowrap rounded-lg z-[9999] font-medium shadow-xl pointer-events-none"
          style={{ left: railTip.x, top: railTip.y }}
        >
          {railTip.label}
          <div className="absolute left-0 top-1/2 -translate-x-[3px] -translate-y-1/2 w-[6px] h-[6px] bg-gray-900/95 rotate-45" />
        </div>
      )}

      {/* SUB-NAVIGATION */}
      {currentActiveSection && (
        <div className="w-[250px] flex-shrink-0 bg-white border-r border-gray-100 flex flex-col z-20 overflow-hidden h-full">
          {/* Header */}
          <div className="px-5 pt-5 pb-4 border-b border-gray-50">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#f6931c]/10 text-[#f6931c]">
                {currentActiveSection.icon}
              </div>
              <div>
                <h2 className="text-[15px] font-bold text-gray-900 tracking-tight">
                  {currentActiveSection.sectionLabel}
                </h2>
                <div className="mt-0.5 h-[2px] w-8 rounded-full" style={{ background: '#f6931c' }} />
              </div>
            </div>
          </div>

          {/* Nav items */}
          <nav
            aria-label={`Platform ${currentActiveSection.sectionLabel} navigation`}
            className="flex flex-col gap-0.5 p-3 flex-1 overflow-y-auto min-h-0 nav-scroll"
          >
            {currentActiveSection.items.filter(item =>
              !item.requirePermission || permissions.includes('*') || permissions.includes(item.requirePermission)
            ).map((leaf) => (
              <NavLink
                key={leaf.to}
                to={leaf.to}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 text-[13px] rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-[#3454cf]/6 text-[#3454cf] font-semibold shadow-[0_0_0_1px_rgba(52,84,207,0.08)_inset]'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 font-medium'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`transition-colors ${isActive ? 'text-[#3454cf]' : 'text-gray-400'}`}>
                      {leaf.icon}
                    </span>
                    <span className="truncate flex-1">{leaf.label}</span>
                    {/* Orange dot for active */}
                    {isActive && (
                      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#f6931c' }} />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Bottom info card with orange accent */}
          <div className="mt-auto mx-3 mb-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #3454cf, #f6931c)' }} />
            <div className="flex items-center gap-2 pt-1">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-[12px] text-gray-500 font-medium">System Online</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
